import { useState, useEffect, useCallback } from 'react';
import { api, ApiError } from '../api/client';
import './DomainLookup.css';

interface Freshness {
  fresh: boolean;
  lastPulledAt: string | null;
  ageSeconds: number | null;
  inflight: boolean;
  runId: string | null;
  staleReason: 'google' | 'microsoft' | 'both' | null;
  google: { pulledAt: string | null; fresh: boolean };
  ms: { pulledAt: string | null; fresh: boolean; error: string | null };
}
interface Mailbox { primaryEmail: string; fullName: string | null; suspended: boolean; aliases: string[]; }
interface SiblingDomain { domain_name: string; is_primary: boolean; verified: boolean; }
interface ResultPanel { inventory_panel_id: string; email: string; reseller_id: string | null; }
interface ResultRow {
  source: 'google' | 'microsoft';
  domain_name: string;
  is_primary: boolean;
  verified: boolean;
  google?: { panels: ResultPanel[] };
  microsoft?: { tenantId: string; tenantName: string };
}
interface SearchResult {
  query: string;
  mode: 'exact' | 'contains';
  emailTarget: string | null;
  found: boolean;
  lastPulledAt: string | null;
  results: ResultRow[];
}
interface MailboxResponse {
  mailboxes: Mailbox[];
  siblingDomains: SiblingDomain[];
  mailboxError: string | null;
}

type SearchMode = 'exact' | 'contains';

interface MailboxState {
  loading: boolean;
  data: MailboxResponse | null;
  error: string | null;
}

function ago(iso: string | null): string {
  if (!iso) return 'never';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s ago`;
}

/** Cache/expand key namespaced by source + the panel email (Google) or tenant
 *  id (Microsoft) so a domain present on both estates never collides. */
function cacheKey(source: 'google' | 'microsoft', domain: string, panelOrTenant: string): string {
  return `${source}|${domain}|${panelOrTenant}`;
}

/** The per-panel/tenant target a single mailbox fetch resolves against. */
type ExpandTarget = { source: 'google'; domain: string; panelEmail: string }
  | { source: 'microsoft'; domain: string; tenantId: string };

function targetKey(t: ExpandTarget): string {
  return t.source === 'google'
    ? cacheKey('google', t.domain, t.panelEmail)
    : cacheKey('microsoft', t.domain, t.tenantId);
}

/** Expand targets for a result row: one per Google panel, or one for the MS tenant. */
function rowTargets(row: ResultRow): ExpandTarget[] {
  if (row.source === 'microsoft' && row.microsoft) {
    return [{ source: 'microsoft', domain: row.domain_name, tenantId: row.microsoft.tenantId }];
  }
  return (row.google?.panels ?? []).map((p) => ({
    source: 'google' as const,
    domain: row.domain_name,
    panelEmail: p.email,
  }));
}

export function DomainLookup(): JSX.Element {
  const [fresh, setFresh] = useState<Freshness | null>(null);
  const [q, setQ] = useState('');
  const [mode, setMode] = useState<SearchMode>('contains');
  const [result, setResult] = useState<SearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [mbx, setMbx] = useState<Record<string, MailboxState>>({});

  const loadFreshness = useCallback(async () => {
    try { setFresh(await api<Freshness>('/api/lookup/freshness')); } catch { /* ignore */ }
  }, []);

  useEffect(() => { void loadFreshness(); }, [loadFreshness]);

  // While a pull is in-flight, poll freshness every 3s so the UI unblocks.
  useEffect(() => {
    if (!fresh?.inflight) return;
    const t = setInterval(() => void loadFreshness(), 3000);
    return () => clearInterval(t);
  }, [fresh?.inflight, loadFreshness]);

  const refresh = async (): Promise<void> => {
    setRefreshing(true);
    setError(null);
    try {
      await api('/api/lookup/refresh', { method: 'POST' });
      await loadFreshness();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchMailboxes = useCallback(async (t: ExpandTarget): Promise<void> => {
    const key = targetKey(t);
    setMbx((prev) => {
      if (prev[key]?.data) return prev; // cached - don't refetch this session
      return { ...prev, [key]: { loading: true, data: null, error: null } };
    });
    const url = t.source === 'google'
      ? `/api/lookup/mailboxes?source=google&domain=${encodeURIComponent(t.domain)}&panelEmail=${encodeURIComponent(t.panelEmail)}`
      : `/api/lookup/mailboxes?source=microsoft&domain=${encodeURIComponent(t.domain)}&tenantId=${encodeURIComponent(t.tenantId)}`;
    try {
      const data = await api<MailboxResponse>(url);
      setMbx((prev) => ({ ...prev, [key]: { loading: false, data, error: null } }));
    } catch (e) {
      if (e instanceof ApiError && e.code === 'STALE_INVENTORY') {
        await loadFreshness();
        setError('Inventory is stale - click Refresh Inventory, then search again.');
        setMbx((prev) => ({ ...prev, [key]: { loading: false, data: null, error: null } }));
        return;
      }
      setMbx((prev) => ({
        ...prev,
        [key]: { loading: false, data: null, error: e instanceof ApiError ? e.message : 'Failed to load mailboxes' },
      }));
    }
  }, [loadFreshness]);

  const toggleExpand = useCallback((t: ExpandTarget): void => {
    const key = targetKey(t);
    setExpanded((prev) => {
      const next = !prev[key];
      if (next && !mbx[key]?.data) void fetchMailboxes(t);
      return { ...prev, [key]: next };
    });
  }, [mbx, fetchMailboxes]);

  const search = useCallback(async (): Promise<void> => {
    if (q.trim().length < 2) return;
    setSearching(true);
    setError(null);
    setResult(null);
    setExpanded({});
    setMbx({});
    try {
      const data = await api<SearchResult>(
        `/api/lookup/search?q=${encodeURIComponent(q.trim())}&mode=${mode}`,
      );
      setResult(data);
      // Auto-expand the single result and fetch its mailboxes (any source).
      if (data.found && data.results.length === 1) {
        const next: Record<string, boolean> = {};
        for (const t of rowTargets(data.results[0])) {
          next[targetKey(t)] = true;
          void fetchMailboxes(t);
        }
        setExpanded(next);
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === 'STALE_INVENTORY') {
        await loadFreshness();
        setError('Inventory is stale - click Refresh Inventory, then search again.');
      } else {
        setError(e instanceof ApiError ? e.message : 'Search failed');
      }
    } finally {
      setSearching(false);
    }
  }, [q, mode, fetchMailboxes, loadFreshness]);

  const switchMode = (m: SearchMode): void => {
    if (m === mode) return;
    setMode(m);
    setResult(null);
    setExpanded({});
    setMbx({});
    setError(null);
  };

  const blocked = !fresh?.fresh;
  const placeholder = mode === 'contains'
    ? 'Search any part of a domain...'
    : 'Enter a full domain or email...';

  // Specific stale notice keyed off the backend's staleReason / ms.error.
  const staleNotice = (): string => {
    if (fresh?.ms.error) return 'Microsoft inventory pull failed - refresh to retry.';
    switch (fresh?.staleReason) {
      case 'microsoft': return 'Microsoft inventory stale - refresh to search.';
      case 'google': return 'Google inventory stale - refresh to search.';
      default: return 'Inventory stale - refresh to search.';
    }
  };

  return (
    <div className="dl-root">
      <div className="dl-scroll">
        <header className="dl-header">
          <h1 className="dl-title">Domain / Email Lookup</h1>
          <p className="dl-subtitle">
            Find which admin panel a domain or email account lives on - across Google
            panels and Microsoft tenants - and list the live mailboxes under it.
          </p>
        </header>

        <section className="dl-card">
          <div className="dl-card-header">
            <h2 className="dl-card-title">Search</h2>
            <div className="dl-card-header-right">
              {fresh?.inflight ? (
                <span className="dl-pill dl-pill--inflight">
                  <span className="dl-pulse" aria-hidden />
                  Refreshing inventory...
                </span>
              ) : fresh?.fresh ? (
                <span className="dl-pill dl-pill--fresh">Fresh - pulled {ago(fresh.lastPulledAt)}</span>
              ) : (
                <span className="dl-pill dl-pill--stale">
                  Stale{fresh?.lastPulledAt ? ` - pulled ${ago(fresh.lastPulledAt)}` : ' - never pulled'}
                </span>
              )}
              <button
                type="button"
                className="dl-btn dl-btn--secondary"
                onClick={() => void refresh()}
                disabled={refreshing || fresh?.inflight}
              >
                {refreshing || fresh?.inflight ? 'Refreshing...' : 'Refresh Inventory'}
              </button>
            </div>
          </div>

          <div className="dl-card-body">
            <div className="dl-kind-tabs">
              <button
                type="button"
                className={`dl-kind-tab ${mode === 'exact' ? 'dl-kind-tab--active' : ''}`}
                onClick={() => switchMode('exact')}
              >
                Exact match
              </button>
              <button
                type="button"
                className={`dl-kind-tab ${mode === 'contains' ? 'dl-kind-tab--active' : ''}`}
                onClick={() => switchMode('contains')}
              >
                Contains
              </button>
            </div>

            <div className="dl-searchbar">
              <input
                className="dl-input"
                type="text"
                placeholder={placeholder}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') void search(); }}
                disabled={blocked}
              />
              <button
                type="button"
                className="dl-btn dl-btn--primary"
                onClick={() => void search()}
                disabled={blocked || searching || q.trim().length < 2}
              >
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>

            {blocked && !fresh?.inflight && (
              <div className="dl-toast dl-toast--info">
                {staleNotice()} Both Google and Microsoft must be parsed within the same
                10-minute window before you can search. Click <strong>Refresh Inventory</strong> to continue.
              </div>
            )}
            {error && <div className="dl-toast dl-toast--error">{error}</div>}
          </div>
        </section>

        {result && !result.found && (
          <section className="dl-card">
            <div className="dl-card-body">
              <div className="dl-toast dl-toast--info">
                <strong className="dl-mono">{result.query}</strong> is not on any admin panel
                {' '}(as of {ago(result.lastPulledAt)}).
              </div>
            </div>
          </section>
        )}

        {result && result.found && (
          <section className="dl-card">
            <div className="dl-card-header">
              <h2 className="dl-card-title">
                Results
                <span className="dl-count">{result.results.length}</span>
              </h2>
            </div>
            <div className="dl-card-body">
              <div className="dl-results">
                {result.results.map((r) => (
                  <DomainCard
                    key={r.source === 'microsoft'
                      ? cacheKey('microsoft', r.domain_name, r.microsoft?.tenantId ?? '')
                      : cacheKey('google', r.domain_name, r.google?.panels[0]?.email ?? '')}
                    row={r}
                    emailTarget={result.emailTarget}
                    expanded={expanded}
                    mbx={mbx}
                    onToggle={toggleExpand}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function DomainCard(props: {
  row: ResultRow;
  emailTarget: string | null;
  expanded: Record<string, boolean>;
  mbx: Record<string, MailboxState>;
  onToggle: (t: ExpandTarget) => void;
}): JSX.Element {
  const { row, emailTarget, expanded, mbx, onToggle } = props;
  const isMs = row.source === 'microsoft';
  const targets = rowTargets(row);
  // A Google domain may sit on multiple panels; expand state is per panel. We
  // treat the card as expanded if any of its targets are open and render one
  // mailbox section per target.
  const anyExpanded = targets.some((t) => expanded[targetKey(t)]);
  const togglePrimary = (): void => { if (targets.length > 0) onToggle(targets[0]); };

  // Panel-identity line: MS shows tenantName (mono); Google shows the email /
  // panel count exactly as before.
  const identity = isMs
    ? row.microsoft?.tenantName ?? ''
    : (row.google?.panels.length === 1
      ? row.google.panels[0].email
      : `${row.google?.panels.length ?? 0} panels`);

  return (
    <div className={`dl-result-card${anyExpanded ? ' dl-result-card--open' : ''}`}>
      <button className="dl-result-head" onClick={togglePrimary}>
        <span className={`dl-chevron${anyExpanded ? ' dl-chevron--open' : ''}`} aria-hidden>›</span>
        <span className="dl-domain dl-mono">{row.domain_name}</span>
        <span className={`dl-source ${isMs ? 'dl-source--ms' : 'dl-source--google'}`}>
          {isMs ? 'Microsoft' : 'Google'}
        </span>
        {row.verified ? (
          <span className="dl-badge dl-badge--verified">Verified</span>
        ) : (
          <span className="dl-badge dl-badge--pending">Pending</span>
        )}
        <span className="dl-tag">{row.is_primary ? 'primary' : 'secondary'}</span>
        <span className="dl-result-panels dl-mono">{identity}</span>
      </button>

      {anyExpanded && (
        <div className="dl-result-body">
          {targets.map((t) => {
            const key = targetKey(t);
            const state = mbx[key];
            const panel = t.source === 'google'
              ? row.google?.panels.find((p) => p.email === t.panelEmail)
              : undefined;
            return (
              <div className="dl-panel-section" key={key}>
                {targets.length > 1 && panel && (
                  <div className="dl-panel-label">
                    <span className="dl-mono">{panel.email}</span>
                    {panel.reseller_id && <span className="dl-tag">reseller {panel.reseller_id}</span>}
                  </div>
                )}

                {!state || state.loading ? (
                  <div className="dl-loading">Loading mailboxes...</div>
                ) : state.error ? (
                  <div className="dl-toast dl-toast--error">{state.error}</div>
                ) : (
                  <MailboxBlock data={state.data} domain={row.domain_name} emailTarget={emailTarget} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MailboxBlock(props: {
  data: MailboxResponse | null;
  domain: string;
  emailTarget: string | null;
}): JSX.Element | null {
  const { data, domain, emailTarget } = props;
  if (!data) return null;
  const siblings = data.siblingDomains.filter((d) => d.domain_name !== domain);

  return (
    <>
      {data.mailboxError && (
        <div className="dl-toast dl-toast--error">{data.mailboxError}</div>
      )}

      {!data.mailboxError && (
        <div className="dl-mailboxes">
          {data.mailboxes.length === 0 ? (
            <div className="dl-empty">No email accounts on this domain.</div>
          ) : (
            <ul className="dl-mbx-list">
              {data.mailboxes.map((m) => {
                const isMatch = emailTarget != null && m.primaryEmail.toLowerCase() === emailTarget;
                return (
                  <li key={m.primaryEmail} className={`dl-mbx${isMatch ? ' dl-mbx--match' : ''}`}>
                    <span className="dl-mbx-email dl-mono">{m.primaryEmail}</span>
                    {isMatch && <span className="dl-match-marker">match</span>}
                    {m.fullName && <span className="dl-mbx-name">{m.fullName}</span>}
                    {m.suspended && <span className="dl-chip dl-chip--suspended">suspended</span>}
                    {m.aliases.length > 0 && (
                      <span className="dl-mbx-aliases">aliases: {m.aliases.join(', ')}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {siblings.length > 0 && (
        <div className="dl-siblings">
          <div className="dl-siblings-title">Also on this panel</div>
          <ul className="dl-sib-list">
            {siblings.map((d) => (
              <li key={d.domain_name} className="dl-sib">
                <span className="dl-mono">{d.domain_name}</span>
                {d.is_primary && <span className="dl-tag">primary</span>}
                {d.verified ? (
                  <span className="dl-dot dl-dot--verified" title="verified" />
                ) : (
                  <span className="dl-dot dl-dot--pending" title="pending" />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
